import { RefreshCw, TriangleAlert } from "lucide-react"
import * as React from "react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Inactive } from "@/components/ui/inactive"
import { Segmented } from "@/components/ui/segmented"
import { Skeleton } from "@/components/ui/skeleton"
import { getConfusionMatrix, PERIODS, type ConfusionMatrix, type Period } from "@/lib/api"
import { EMPTY, formatInt, formatMoney, formatPercent } from "@/lib/format"
import { hasMatrixData, matrixGrid, matrixMetrics, OUTCOMES, type MatrixOutcome } from "@/lib/matrix"
import { cn } from "@/lib/utils"

const PERIOD_OPTIONS = PERIODS.map((period) => ({ value: period, label: period }))

const TONE: Record<MatrixOutcome, "approve" | "review" | "decline"> = {
  approve: "approve",
  review: "review",
  decline: "decline",
}

/**
 * Панель матрицы ошибок и трёх производных показателей.
 *
 * Матрица — только из ручки. Пока размеченных транзакций нет (разметки
 * fraud / не fraud в консоли ещё нет), панель говорит это прямо вместо сетки
 * нулей: девять нулей и три прочерка читались бы как «движок не ошибается».
 * Показатели считаются из **той** матрицы, которая на экране (`lib/matrix.ts`),
 * а не берутся отдельным набором цифр: иначе карточки и сетка могли бы
 * разойтись между собой.
 */
export function MatrixPanel() {
  const [period, setPeriod] = React.useState<Period>("30D")
  const [matrix, setMatrix] = React.useState<ConfusionMatrix | null>(null)
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)
  const [reload, setReload] = React.useState(0)

  React.useEffect(() => {
    const controller = new AbortController()
    let ignore = false
    setLoading(true)

    getConfusionMatrix({ period }, controller.signal)
      .then((live) => {
        if (ignore) return
        setMatrix(live)
        setError(null)
      })
      .catch((cause: unknown) => {
        if (ignore || controller.signal.aborted) return
        setError(cause instanceof Error ? cause.message : "Request failed")
      })
      .finally(() => {
        if (!ignore) setLoading(false)
      })

    return () => {
      ignore = true
      controller.abort()
    }
  }, [period, reload])

  const grid = matrix ? matrixGrid(matrix) : null
  const metrics = matrix ? matrixMetrics(matrix) : null
  const currency = matrix?.currency ?? EMPTY
  const unlabeled = matrix !== null && !hasMatrixData(matrix)

  return (
    <section className="animate-panel-in rounded border border-line bg-surface shadow-panel">
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-line px-4 py-3">
        <div className="flex items-center gap-2">
          <h2 className="card-title">Confusion Matrix</h2>
          <button
            type="button"
            onClick={() => setReload((value) => value + 1)}
            aria-label="Reload confusion matrix"
            className="text-fg-subtle transition-colors duration-150 hover:text-accent"
          >
            <RefreshCw className={loading ? "h-3.5 w-3.5 animate-spin" : "h-3.5 w-3.5"} strokeWidth={2} />
          </button>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Inactive reason={`The data set is denominated in ${currency} only.`}>
            Currency: {currency}
          </Inactive>
          <Segmented
            label="Confusion matrix period"
            options={PERIOD_OPTIONS}
            value={period}
            onChange={setPeriod}
          />
        </div>
      </header>

      {error && (
        <div className="px-4 py-10 text-center">
          <p className="flex items-center justify-center gap-2 text-sm font-medium text-decline">
            <TriangleAlert className="h-4 w-4" strokeWidth={2} />
            {error}
          </p>
          <Button className="mt-4" onClick={() => setReload((value) => value + 1)}>
            Retry
          </Button>
        </div>
      )}

      {!error && unlabeled && (
        <div className="px-4 py-10 text-center">
          <p className="text-sm font-medium text-fg">No labeled transactions yet</p>
          <p className="mx-auto mt-1 max-w-md text-xs leading-relaxed text-fg-muted">
            The matrix compares engine decisions with confirmed fraud / not fraud labels. It fills in
            once transactions in this period are labeled.
          </p>
        </div>
      )}

      {!error && !unlabeled && (
        <div className="overflow-x-auto p-4">
          <table className="w-full border-separate border-spacing-0 text-sm">
            <thead>
              <tr>
                <th className="w-44 border-b border-line px-3 py-2" />
                {OUTCOMES.map((predicted) => (
                  <th
                    key={predicted}
                    scope="col"
                    className="stencil-label border-b border-line px-3 py-2 text-left"
                  >
                    <span className="inline-flex items-center gap-1.5">
                      <Dot tone={TONE[predicted]} />
                      Seon {predicted}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {OUTCOMES.map((actual, row) => (
                <tr key={actual}>
                  <th scope="row" className="stencil-label border-b border-line px-3 py-3 text-left">
                    <span className="inline-flex items-center gap-1.5">
                      <Dot tone={TONE[actual]} />
                      Actual {actual}
                    </span>
                  </th>
                  {OUTCOMES.map((predicted, column) => {
                    const cell = grid?.[row][column] ?? null
                    const diagonal = row === column
                    return (
                      <td
                        key={predicted}
                        className={cn(
                          "border-b border-line px-3 py-3 align-top",
                          diagonal && "bg-surface-sunken",
                        )}
                      >
                        {loading && !matrix ? (
                          <Skeleton className="h-8 w-24" />
                        ) : (
                          <>
                            <div className="flex items-center gap-2">
                              <span className="text-lg tabular-nums text-fg">
                                {formatInt(cell?.count)}
                              </span>
                              {cell?.label && (
                                <Badge tone={diagonal ? "approve" : "decline"}>{cell.label}</Badge>
                              )}
                            </div>
                            <div className="mt-0.5 text-xs text-fg-subtle">
                              {cell ? formatMoney(cell.amount, cell.currency) : EMPTY}
                            </div>
                          </>
                        )}
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
          </table>

          <div className="mt-4 grid gap-3 sm:grid-cols-3">
            <MetricCard
              label="Accuracy"
              hint="Correct decisions across all nine cells."
              value={metrics?.accuracy ?? null}
              loading={loading && !matrix}
            />
            <MetricCard
              label="Precision · decline"
              hint="Of everything the engine declined, how much was actually fraud."
              value={metrics?.precision ?? null}
              loading={loading && !matrix}
            />
            <MetricCard
              label="Recall · decline"
              hint="Of all actual fraud, how much the engine declined."
              value={metrics?.recall ?? null}
              loading={loading && !matrix}
            />
          </div>
        </div>
      )}
    </section>
  )
}

function MetricCard({
  label,
  hint,
  value,
  loading,
}: {
  label: string
  hint: string
  value: number | null
  loading: boolean
}) {
  return (
    <div className="rounded border border-line bg-surface-sunken px-4 py-3">
      <p className="stencil-label">{label}</p>
      {loading ? (
        <Skeleton className="mt-2 h-7 w-20" />
      ) : (
        <p className="mt-1 text-2xl tabular-nums text-fg">
          {value === null ? EMPTY : formatPercent(value)}
        </p>
      )}
      <p className="mt-1 text-xs leading-snug text-fg-muted">{hint}</p>
    </div>
  )
}

function Dot({ tone }: { tone: "approve" | "review" | "decline" }) {
  return (
    <span
      aria-hidden
      className={cn(
        "inline-block h-1.5 w-1.5 rounded-full",
        tone === "approve" && "bg-approve",
        tone === "review" && "bg-review",
        tone === "decline" && "bg-decline",
      )}
    />
  )
}
